import React, { useCallback, useEffect, useState } from "react";
import {
  Avatar, Box, Button, Chip, CircularProgress, Container, IconButton,
  Paper, Tooltip, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import {
  EmailThread, EmailThreadStatus, PipelineColumn,
  fetchPipeline, updateThreadStatus,
} from "./emailHubApi";
import { useNavigate } from "react-router-dom";

const T = {
  navy: "#1E4D8F", gold: "#D4AF37", goldLight: "#f5eed9",
  white: "#fff", cream: "#faf8f4", bg: "#f4f2ee",
  slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  green: "#2d6a35", greenBg: "#edf7ee",
  blue: "#1e40af", blueBg: "#eff6ff",
  red: "#9b2c2c", redBg: "#fef2f2",
};

const fmtDate = (d: string | null) => {
  if (!d) return "";
  const dt = new Date(d);
  const now = new Date();
  const diff = now.getTime() - dt.getTime();
  if (diff < 86400000 && dt.getDate() === now.getDate()) return dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const initials = (name: string | null, email: string) => (name && name.length >= 2) ? name.slice(0, 2).toUpperCase() : email.slice(0, 2).toUpperCase();

/* ═══════════════════════════════════════════════════════════════════════════ */
const EmailPipeline: React.FC = () => {
  const navigate = useNavigate();
  const [columns, setColumns] = useState<PipelineColumn[]>([]);
  const [loading, setLoading] = useState(true);
  const [dragThread, setDragThread] = useState<EmailThread | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await fetchPipeline(); setColumns(d.columns || []); } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDrop = async (statusId: number) => {
    if (!dragThread) return;
    if (dragThread.status_id === statusId) return;
    try {
      await updateThreadStatus(dragThread.id, statusId);
      // Optimistic update
      setColumns(prev => prev.map(col => ({
        ...col,
        threads: col.status.id === statusId
          ? [...col.threads, { ...dragThread, status_id: statusId }]
          : col.threads.filter(t => t.id !== dragThread.id),
        count: col.status.id === statusId ? col.count + 1 :
               col.threads.some(t => t.id === dragThread.id) ? col.count - 1 : col.count,
      })));
    } catch {}
    setDragThread(null);
  };

  return (
    <Container maxWidth={false} disableGutters sx={{ px: 3, py: 3, height: "calc(100vh - 70px)", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton onClick={() => navigate("/email-hub")} sx={{ color: T.slateLt }}><ArrowBackIcon sx={{ fontSize: 20 }} /></IconButton>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: T.navy }}>Email Pipeline</Typography>
        </Box>
        <Button startIcon={<RefreshIcon sx={{ fontSize: 14 }} />} onClick={load} size="small"
          sx={{ textTransform: "none", fontSize: 12, color: T.slate }}>Refresh</Button>
      </Box>

      {/* Kanban Board */}
      {loading ? (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress sx={{ color: T.gold }} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: "flex", gap: 2, overflow: "auto", pb: 2 }}>
          {columns.map(col => (
            <Box key={col.status.id}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.backgroundColor = "#f5f3ff"; }}
              onDragLeave={e => { e.currentTarget.style.backgroundColor = ""; }}
              onDrop={e => { e.preventDefault(); e.currentTarget.style.backgroundColor = ""; handleDrop(col.status.id); }}
              sx={{ width: 300, flexShrink: 0, display: "flex", flexDirection: "column", borderRadius: 3, bgcolor: T.bg, border: `1px solid ${T.line}`, overflow: "hidden" }}>

              {/* Column Header */}
              <Box sx={{ px: 2, py: 1.5, bgcolor: T.white, borderBottom: `2px solid ${col.status.color}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: col.status.color }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.navy }}>{col.status.name}</Typography>
                </Box>
                <Chip label={col.count} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: col.status.color + "20", color: col.status.color }} />
              </Box>

              {/* Cards */}
              <Box sx={{ flex: 1, overflow: "auto", p: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                {col.threads.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: "center" }}>
                    <InboxOutlinedIcon sx={{ fontSize: 28, color: T.slateLt, opacity: 0.3 }} />
                    <Typography sx={{ fontSize: 11, color: T.slateLt, mt: 0.5 }}>No threads</Typography>
                  </Box>
                ) : col.threads.map(thread => {
                  const latest = thread.latest_email;
                  return (
                    <Paper key={thread.id} draggable
                      onDragStart={() => setDragThread(thread)}
                      onClick={() => navigate("/email-hub")} // Navigate back to inbox
                      sx={{
                        p: 1.5, borderRadius: 2, cursor: "grab", border: `1px solid ${T.line}`,
                        bgcolor: T.white, transition: "all 0.15s",
                        "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)", borderColor: T.gold },
                        "&:active": { cursor: "grabbing" },
                      }}>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 10, fontWeight: 700, bgcolor: thread.unread_count > 0 ? T.navy : T.slateLt }}>
                          {initials(latest?.from_name ?? null, latest?.from_email || "")}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography noWrap sx={{ fontSize: 11, fontWeight: 600, color: T.navy, flex: 1 }}>
                              {latest?.from_name || latest?.from_email || "Unknown"}
                            </Typography>
                            <Typography sx={{ fontSize: 9, color: T.slateLt }}>{fmtDate(thread.last_message_at)}</Typography>
                          </Box>
                          <Typography noWrap sx={{ fontSize: 11, color: T.slate, mt: 0.2 }}>{thread.subject}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5, mt: 0.8, flexWrap: "wrap" }}>
                        {thread.unread_count > 0 && <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: T.blue, mt: 0.5 }} />}
                        {thread.is_overdue && <Chip label="SLA" size="small" sx={{ height: 14, fontSize: 8, fontWeight: 700, bgcolor: T.redBg, color: T.red }} />}
                        {thread.ai_intent && <Chip label={thread.ai_intent} size="small" sx={{ height: 14, fontSize: 8, fontWeight: 600, bgcolor: T.bg, color: T.slate }} />}
                        {thread.message_count > 1 && <Chip label={`${thread.message_count}`} size="small" sx={{ height: 14, fontSize: 8, bgcolor: T.bg, color: T.slateLt }} />}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Container>
  );
};

export default EmailPipeline;
